import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import { createFlow, updateFlow, fetchHrAssignees, invokeFlow } from '../../../Redux/ActionCreators/onboardingAction';
import OnboardingStepper from '../../../utils/OnboardingStepper/OnboardingStepper';
import FlowSetupStep from './steps/FlowSetupStep';
import FormsStep from './steps/FormsStep';
import DocumentsStep from './steps/DocumentsStep';
import ReviewStep from './steps/ReviewStep';
import '../onboarding.scss';
import { useAppPermission } from '../../../hooks/useAppPermission';
import { P } from '../../../constants/rbac';

const STEPS = ['Flow overview', 'Forms & Documents', 'Company Documents', 'Review'];

const defaultFormData = {
  name: '',
  description: '',
  portal_name: '',
  subdomain: '',
  status: 'draft',
  hr_assigned_user_id: '',
  step_flags: { welcome: true, forms: true, documents: true },
  welcome: {
    title: "Welcome to {{company_name}} — We're thrilled to have you!",
    message: "This onboarding portal will guide you through everything you need to complete before your first day. Please fill out the forms, review the policies, and reach out if you have any questions.",
    videoUrl: '',
  },
  forms: { selectedForms: [] },
  documents: { selectedDocuments: [] },
};

const getNextStep = (current, stepFlags) => {
  let next = current + 1;
  if (next === 1 && !stepFlags.forms) next++;
  if (next === 2 && !stepFlags.documents) next++;
  return next;
};

const getPrevStep = (current, stepFlags) => {
  let prev = current - 1;
  if (prev === 2 && !stepFlags.documents) prev--;
  if (prev === 1 && !stepFlags.forms) prev--;
  return prev;
};

const slugify = (s) =>
  (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/--+/g, '-').slice(0, 128) || 'onboarding';

const CreateFlow = ({ flowData, onBack, onSaved }) => {
  const dispatch = useDispatch();
  const { can } = useAppPermission();
  const isEdit = Boolean(flowData?.id);
  const canSave = isEdit ? can(P.ONBOARDING_FLOW_EDIT) : can(P.ONBOARDING_FLOW_CREATE);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(defaultFormData);
  const [flowId, setFlowId] = useState(flowData?.id || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hrAssignees, setHrAssignees] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!canSave) {
      onBack?.();
    }
  }, [canSave, onBack]);

  useEffect(() => {
    dispatch(fetchHrAssignees()).then(res => {
      setHrAssignees(res?.data || []);
    });
  }, [dispatch]);

  useEffect(() => {
    if (flowData) {
      setFlowId(flowData.id || null);
      const sf = flowData.step_flags || { welcome: true, forms: true, documents: true };
      const wc = flowData.welcome_content || {};
      const docIds = flowData.document_ids || [];
      const docModes = flowData.document_modes || {};
      setFormData({
        name: flowData.name || '',
        description: flowData.description || '',
        portal_name: flowData.portal_name || '',
        subdomain: flowData.subdomain || '',
        status: flowData.status || 'draft',
        hr_assigned_user_id: flowData.hr_assigned_user_id || '',
        step_flags: {
          welcome: sf.welcome !== false,
          forms: sf.forms !== false,
          documents: sf.documents !== false,
        },
        welcome: {
          title: wc.title || '',
          message: wc.message || '',
          videoUrl: wc.video_url || wc.videoUrl || '',
        },
        forms: { selectedForms: flowData.attached_form_ids || [] },
        documents: {
          selectedDocuments: docIds.map(id => ({ id, mode: docModes[String(id)] || 'read' })),
        },
      });
    }
  }, [flowData]);

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const stepFlags = formData.step_flags || { welcome: true, forms: true, documents: true };

  const skippedSteps = [
    !stepFlags.forms ? 1 : null,
    !stepFlags.documents ? 2 : null,
  ].filter(n => n !== null);

  const buildPayload = (status) => {
    const selectedDocs = formData.documents.selectedDocuments || [];
    return {
      name: formData.name,
      description: formData.description || null,
      portal_name: `${formData.name} Portal`,
      subdomain: slugify(formData.name),
      status,
      hr_assigned_user_id: formData.hr_assigned_user_id ? Number(formData.hr_assigned_user_id) : null,
      step_flags: { ...formData.step_flags, checklist: false },
      welcome_content: formData.step_flags.welcome ? {
        title: formData.welcome.title,
        message: formData.welcome.message,
        video_url: formData.welcome.videoUrl,
      } : null,
      attached_form_ids: formData.forms.selectedForms,
      document_ids: selectedDocs.map(d => d.id),
      document_modes: Object.fromEntries(selectedDocs.map(d => [d.id, d.mode])),
      checklist_tasks: [],
    };
  };

  const handleNext = async () => {
    setError('');
    if (step === 0) {
      if (!formData.name.trim()) { setError('Flow name is required.'); return; }
      setSaving(true);
      let res;
      if (flowId) {
        res = await dispatch(updateFlow(flowId, buildPayload('draft')));
      } else {
        res = await dispatch(createFlow(buildPayload('draft')));
        if (res?.id) setFlowId(res.id);
        else if (res?.data?.id) setFlowId(res.data.id);
      }
      setSaving(false);
      if (!res) { setError('Failed to save flow.'); return; }
      setSnackbar({ open: true, message: 'Flow saved successfully.', severity: 'success' });
    } else if (flowId) {
      setSaving(true);
      const updateRes = await dispatch(updateFlow(flowId, buildPayload('draft')));
      setSaving(false);
      if (updateRes) setSnackbar({ open: true, message: 'Flow saved successfully.', severity: 'success' });
    }
    setStep(getNextStep(step, stepFlags));
  };

  const handleBack = () => {
    if (step === 0) { onBack(); return; }
    setStep(getPrevStep(step, stepFlags));
  };

  const handleFinish = async (status) => {
    if (!flowId) { setError('Flow not created yet. Go back to step 1 and click Next.'); return; }
    setSaving(true);
    setError('');
    const res = await dispatch(updateFlow(flowId, buildPayload(status)));
    setSaving(false);
    if (res) {
      if (status === 'active') {
        setPublishSuccess(true);
      } else {
        setSnackbar({ open: true, message: 'Flow saved as draft.', severity: 'success' });
        onSaved();
      }
    } else {
      setError('Failed to save flow.');
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    const res = await dispatch(invokeFlow({
      flow_id: flowId,
      initiation_type: 'test',
      skip_otp: true,
      skip_invite_email: true,
      use_recruiter_identity: true,
      joining_date: new Date().toISOString().split('T')[0],
    }));
    setPreviewing(false);
    if (res) {
      const data = res?.data ?? res;
      const token = data?.portal_session?.access_token;
      if (token) {
        window.open(`/onboarding/test-flow?token=${encodeURIComponent(token)}&name=${encodeURIComponent(formData.name)}`, '_blank');
      } else {
        alert('Could not start test session. Please try again.');
      }
    }
  };

  const stepProps = { formData, update, error, hrAssignees };

  const renderStep = () => {
    switch (step) {
      case 0: return <FlowSetupStep {...stepProps} />;
      case 1: return <FormsStep {...stepProps} />;
      case 2: return <DocumentsStep {...stepProps} />;
      case 3: return <ReviewStep {...stepProps} />;
      default: return null;
    }
  };

  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="ob-page">
      <div className="ob-page__header">
        <div className="ob-page__header-left">
          <h2>{formData.name || (flowData ? 'Edit Flow' : 'Create Onboarding Flow')}</h2>
          <p>Configure each step and save when ready</p>
        </div>
        <button className="ob-btn" onClick={onBack}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Flows
        </button>
      </div>

      <div className="ob-card" style={{ marginBottom: 20 }}>
        <OnboardingStepper
          steps={STEPS}
          currentStep={step}
          skippedSteps={skippedSteps}
          onStepClick={(flowData || flowId) ? (i) => { setError(''); setStep(i); } : undefined}
        />
      </div>

      <div className="ob-card">
        {renderStep()}

        {error && (
          <div className="ob-hint ob-hint--warn" style={{ marginTop: 12 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><circle cx="12" cy="16" r="1" fill="currentColor" />
            </svg>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, paddingTop: 20, borderTop: '1px solid #e4e9f0' }}>
          <button className="ob-btn" onClick={handleBack} disabled={saving}>
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            {!isLastStep ? (
              <button className="ob-btn ob-btn--primary" onClick={handleNext} disabled={saving}>
                {saving ? 'Saving…' : 'Next'}
                {!saving && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                  </svg>
                )}
              </button>
            ) : canSave ? (
              <>
                <button className="ob-btn" onClick={() => handleFinish('draft')} disabled={saving}>
                  {saving ? 'Saving…' : 'Save as draft'}
                </button>
                <button className="ob-btn ob-btn--primary" onClick={() => handleFinish('active')} disabled={saving}>
                  {saving ? 'Saving…' : 'Publish'}
                  {!saving && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 2L11 13" /><path d="M22 2L15 22l-4-9-9-4 20-7z" />
                    </svg>
                  )}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {publishSuccess && (
        <div className="ob-modal-overlay">
          <div className="ob-modal" style={{ maxWidth: 420, textAlign: 'center' }}>
            <div style={{ marginBottom: 12 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
            <h3 style={{ marginBottom: 8 }}>Flow Published!</h3>
            <p style={{ fontSize: 13, color: '#525b66', marginBottom: 24 }}>
              Your onboarding flow <strong>{formData.name}</strong> is now live. You can preview the candidate portal or return to the flows list.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="ob-btn" onClick={onSaved}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
                </svg>
                Back to Flows
              </button>
              <button
                className="ob-btn ob-btn--primary"
                onClick={handlePreview}
                disabled={previewing}
                style={{ color: '#1e40af', borderColor: '#bfdbfe', background: '#eff6ff' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                {previewing ? 'Opening…' : 'Preview candidate portal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateFlow;
